import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import ComboChart from './ComboChart';
import KpiStat from './KpiStat';
import SummaryTable from './SummaryTable';
import { monthLabel, monthLabelLong, deltaPoints, deltaCount, deltaDir } from '../helpers';
// import { monthShort } from '../helpers';

const CALL = '#4D8AFF';

const useStyles = makeStyles((theme) => ({
	card: { overflow: 'hidden' },
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		padding: theme.spacing(1.5, 2),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	titleWrap: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
	dot: { width: 8, height: 8, borderRadius: '50%', flex: 'none', backgroundColor: CALL },
	title: { fontSize: 13, fontWeight: 700, letterSpacing: 0.2 },
	// pstat: { fontSize: 12, color: theme.palette.text.secondary, fontVariantNumeric: 'tabular-nums' },
	// pstatVal: { color: theme.palette.text.primary, fontWeight: 700 },
	body: { padding: theme.spacing(2) },
	chart: { position: 'relative', height: 180, marginBottom: theme.spacing(2) },
	spacer: { height: theme.spacing(1.5) },
	subhead: {
		fontSize: 11,
		fontWeight: 700,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(1),
	},
}));

export default function EfficiencyModule({ data, loading, language, prevMonthKey /* , month */ }) {
	const classes = useStyles();
	const { trend = [], current = {}, previous = {}, breakdown = [] } = data || {};
	const labels = trend.map((t) => monthLabel(t.month));
	const rateDelta = deltaPoints(current.rate, previous.rate);
	const attDelta = deltaCount(current.attended, previous.attended);
	const schDelta = deltaCount(current.scheduled, previous.scheduled);
	const rateWarn = current.rate !== null && current.rate > 100;
	const vsPrev = `${language.vs} ${monthLabelLong(prevMonthKey)}`;

	return (
		<Paper className={classes.card} elevation={1}>
			<div className={classes.header}>
				<div className={classes.titleWrap}>
					<span className={classes.dot} />
					<Typography className={classes.title}>{language.team_efficiency}</Typography>
				</div>
				{/* <Typography className={classes.pstat}>
					{monthShort(month)}{' '}
					<span className={classes.pstatVal}>{current.rate === null ? '—' : current.rate + '%'}</span>
					{'  ·  '}
					{monthShort(prevMonthKey)} {previous.rate === null ? '—' : previous.rate + '%'}
				</Typography> */}
			</div>
			{loading && <LinearProgress />}
			<div className={classes.body}>
				<div className={classes.chart}>
					<ComboChart
						labels={labels}
						bars={[
							{ label: language.scheduled, data: trend.map((t) => t.scheduled), color: 'rgba(107,127,163,0.6)' },
							{ label: language.attended_label, data: trend.map((t) => t.attended), color: 'rgba(77,138,255,0.7)' },
						]}
						line={{ label: language.called_rate, data: trend.map((t) => t.rate), color: CALL }}
					/>
				</div>
				<Grid container spacing={1}>
					<Grid item xs={4}>
						<KpiStat
							label={language.called_rate}
							value={current.rate === null ? '—' : current.rate + '%'}
							delta={rateDelta === null ? null : (rateDelta > 0 ? '+' : '') + rateDelta + '%'}
							deltaLabel={vsPrev}
							dir={deltaDir(rateDelta)}
							warn={rateWarn}
						/>
					</Grid>
					<Grid item xs={4}>
						<KpiStat
							label={language.attended_label}
							value={current.attended}
							delta={(attDelta > 0 ? '+' : '') + attDelta}
							deltaLabel={vsPrev}
							dir={deltaDir(attDelta)}
						/>
					</Grid>
					<Grid item xs={4}>
						<KpiStat
							label={language.scheduled}
							value={current.scheduled}
							delta={(schDelta > 0 ? '+' : '') + schDelta}
							deltaLabel={vsPrev}
							dir={deltaDir(schDelta)}
						/>
					</Grid>
				</Grid>
				<div className={classes.spacer} />
				<Typography className={classes.subhead}>{language.summary_by_agent}</Typography>
				<SummaryTable
					rows={breakdown}
					nameLabel={language.salesman}
					countCols={[
						{ name: 'scheduled', label: language.scheduled },
						{ name: 'attended', label: language.attended_label },
					]}
					rateLabel={language.called_rate}
					vsLabel={vsPrev}
					barVariant="efficiency"
				/>
			</div>
		</Paper>
	);
}
